import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  Injector,
  input,
  OnInit,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  FloatLabelType,
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, first, startWith } from 'rxjs';
import { ErrorFieldComponent } from '../error-field/error-field.component';
import { Item } from '../select-field/select-field.component';

@Component({
  selector: 'siat-multiselect-field',
  templateUrl: './multiselect-field.component.html',
  styleUrls: ['./multiselect-field.component.scss'],
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    TranslatePipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectFieldComponent implements OnInit, ControlValueAccessor {
  /**
   * The form group associated with the input field.
   */
  formGroup = input<FormGroup>();

  /**
   * The control name associated with the input field.
   */
  controlName = input<string>('');

  /**
   * The id attribute of the input field.
   */
  id = input<string>('');

  /**
   * Th placeholder of the input field.
   */
  placeholder = input<string>('');

  /**
   * The input label.
   */
  label = input<string>('');

  /**
   * The input "name" attribute value.
   */
  name = input<string>('');

  /**
   * Items input.
   */
  items = input<Item[]>([]);

  /**
   * The appearance of the form field.
   */
  appearance = input<MatFormFieldAppearance>('outline');

  /**
   * The floating label behavior of the form field.
   */
  floatLabel = input<FloatLabelType>('auto');

  /**
   * The disabled state of the form field.
   */
  disabledField = input<boolean>(false);

  /**
   * Shows the select/deselect all toggle when there are at least two options.
   */
  withSelectAll = input<boolean>(false);

  /**
   * The injector.
   */
  private injector = inject(Injector);

  /**
   * The disabled ids.
   */
  disabledIds = input<number[] | string[]>();

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    const value$ = this.control.valueChanges.pipe(
      startWith(this.control.value || []),
    );
    const items$ = toObservable(this.items, { injector: this.injector });
    const combined$ = combineLatest([value$, items$]);

    combined$
      .pipe(first(([_value, items]) => items.length > 0))
      .subscribe(([_value, items]) => {
        const currentValue = this.control.value;

        if (currentValue?.length > 0 && Array.isArray(currentValue)) {
          const values = [];

          for (const value of currentValue) {
            if (typeof value === 'string' && value) {
              const matchingItem = items.find((item) => item?.name === value);
              values.push(matchingItem);
            } else if (typeof value === 'number' && value) {
              const matchingItem = items.find((item) => item?.id === value);
              values.push(matchingItem);
            } else if (typeof value === 'object' && value?.id != null) {
              const matchingItem = items.find((item) => item?.id === value.id);
              values.push(matchingItem ?? value);
            }
          }
          this.control.setValue(values, { emitEvent: true });
        }
      });
  }

  /**
   * Gets the form control associated with the input field.
   */
  get control(): FormControl<Item[]> {
    return this.formGroup().controls[this.controlName()] as FormControl<Item[]>;
  }

  /**
   * Gets a flag telling if the the form control is in a disabled state.
   */
  get disabled(): boolean {
    return this.control.disabled;
  }

  /**
   * Keeps a reference to the function to be called every time
   * the value of the field is changed in the UI.
   */
  onChange: (value: Item[]) => void = () => {};

  /**
   * Keeps a reference to the function to be called every time
   * the field has to be considered touched (normally on blur).
   */
  onTouched: () => void = () => {};

  /**
   * Registers a function to call when the field value changes in the UI.
   * This is needed to communicate the update to angular forms API.
   */
  registerOnChange(changeCallback: (value: Item[]) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * Allows to register a function to be called when the field is touched
   * (it's a "noop" method but it's needed to correctly implement ControlValueAccessor interface).
   */
  registerOnTouched(touchedCallback: () => void) {
    this.onTouched = touchedCallback;
  }

  /**
   * @inheritDoc
   */
  writeValue(value: Item[]): void {
    this.control.setValue(value);
  }

  /**
   * @inheritDoc
   */
  compareItems = (a: Item, b: Item) => a?.id === b?.id;

  /**
   * True when the select/deselect all toggle is enabled and there are at least two options.
   * @returns {boolean}
   */
  get showSelectAll(): boolean {
    return (
      this.withSelectAll() &&
      !this.disabledField() &&
      (this.items()?.length ?? 0) >= 2
    );
  }

  /**
   * True when every enabled option is selected.
   * @returns {boolean}
   */
  get allSelected(): boolean {
    const selectable = this.selectableItems;
    return (
      selectable.length > 0 && selectable.every((item) => this.isSelected(item))
    );
  }

  /**
   * True when only part of the enabled options is selected.
   * @returns {boolean}
   */
  get someSelected(): boolean {
    return (
      !this.allSelected &&
      this.selectableItems.some((item) => this.isSelected(item))
    );
  }

  /**
   * Selects all the enabled options, or deselects them when they are all selected.
   * Selected disabled options are always kept.
   */
  toggleSelectAll(): void {
    const selectable = this.selectableItems;
    const locked = (this.control.value ?? []).filter(
      (value) => !selectable.some((item) => this.compareItems(value, item)),
    );
    this.control.setValue(
      this.allSelected ? locked : [...locked, ...selectable],
    );
    this.control.markAsDirty();
  }

  /**
   * Options that can be toggled by the user.
   * @returns {Item[]}
   */
  private get selectableItems(): Item[] {
    return (this.items() ?? []).filter(
      (item) => !!item && !this.isOptionDisabled(item),
    );
  }

  /**
   * True when the given option is in the current value.
   * @returns {boolean}
   */
  private isSelected(item: Item): boolean {
    return (this.control.value ?? []).some((value) =>
      this.compareItems(value, item),
    );
  }

  /**
   * Checks if the option is disabled.
   * @param option
   */
  isOptionDisabled(option: Item): boolean {
    if (!option) return false;

    const idToCheck = option.id;
    const listOfDisabledIds = this.disabledIds();

    if (!listOfDisabledIds || listOfDisabledIds.length === 0) {
      return false;
    }

    return listOfDisabledIds.some((disabledId) => disabledId == idToCheck);
  }
}
